import { BRAND, SUBPROCESSORS } from '../marketingCopy';

function LegalShell({ title, children }) {
  return (
    <article className="prose-invert max-w-3xl space-y-4 text-sm leading-relaxed text-slate-300">
      <p className="text-xs uppercase tracking-wide text-slate-500">Effective {BRAND.lastUpdated}</p>
      <h1 className="text-3xl font-semibold text-orca-strong">{title}</h1>
      {children}
    </article>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy policy">
      <p>
        {BRAND.company} (“SLP”, “we”) operates {BRAND.product} (“Orca”). This policy describes personal data we process
        when you use the marketing site, request a demo, or sign in to the application.
      </p>
      <h2 className="text-lg font-semibold text-orca-strong">What we collect</h2>
      <ul className="list-disc pl-5">
        <li>Account data: username, email, password hash (or SSO subject from WorkOS).</li>
        <li>Demo requests: name, work email, company, role, optional Fusion host, message.</li>
        <li>Usage: session cookies, copilot prompt/response audit (for customers using the app).</li>
        <li>Customer-provided Fusion credentials: stored encrypted at rest when saved as a Connection.</li>
      </ul>
      <h2 className="text-lg font-semibold text-orca-strong">How we use it</h2>
      <p>
        To authenticate you, connect to the Fusion pod you designate, provide copilots and admin tools, respond to demo
        requests, and secure the service. We do not sell personal data.
      </p>
      <h2 className="text-lg font-semibold text-orca-strong">LLM processing</h2>
      <p>
        Prompts and retrieved HCM context may be sent to the LLM provider configured for that deployment. Preferred
        production path is OCI Generative AI in the customer’s region. Some deployments currently use DeepSeek. The
        order form / data-flow exhibit for each customer states which provider is in force. See Trust &amp; data flow.
      </p>
      <h2 className="text-lg font-semibold text-orca-strong">Cookies</h2>
      <p>
        Essential session cookies only (signed, HttpOnly). Theme preference is stored in localStorage on the device.
      </p>
      <h2 className="text-lg font-semibold text-orca-strong">Retention</h2>
      <p>
        Demo requests: until the opportunity closes plus a reasonable sales archive. App accounts and audit logs: for
        the subscription term and as required for security investigations.
      </p>
      <h2 className="text-lg font-semibold text-orca-strong">Contact</h2>
      <p>
        Privacy questions: use the demo form or contact {BRAND.company} via{' '}
        <a className="text-cyan-400" href={BRAND.siteUrl}>
          {BRAND.siteUrl}
        </a>
        .
      </p>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms of use">
      <p>
        These terms cover the public marketing site and, when you subscribe, the Orca application. Paid use is also
        governed by an order form. If they conflict, the order form wins.
      </p>
      <h2 className="text-lg font-semibold text-orca-strong">The service</h2>
      <p>
        Orca is a specialist workbench for Oracle Fusion HCM security and approvals. It is not a CNAPP, not identity
        governance for the whole enterprise, and not affiliated with Orca Security.
      </p>
      <h2 className="text-lg font-semibold text-orca-strong">Your Fusion tenant</h2>
      <p>
        You are responsible for the integration user, privileges, and whether Orca may write (deploy drafts, revoke
        roles). You must use a non-production pod for evaluation unless an order says otherwise.
      </p>
      <h2 className="text-lg font-semibold text-orca-strong">Acceptable use</h2>
      <p>No sharing of credentials, no attacking the service, no uploading unlawful data, no scraping the copilots.</p>
      <h2 className="text-lg font-semibold text-orca-strong">Warranties and liability</h2>
      <p>
        The marketing site is provided as-is. Paid service warranties, indemnities, and liability caps are in the order
        form. Fusion remains Oracle’s system of record.
      </p>
      <h2 className="text-lg font-semibold text-orca-strong">Governing law</h2>
      <p>State of Michigan, USA, unless the order form specifies otherwise.</p>
    </LegalShell>
  );
}

export function DpaPage() {
  return (
    <LegalShell title="Data processing addendum (outline)">
      <p>
        This page is the public DPA outline. A signed DPA is available on request and is attached to enterprise orders.
      </p>
      <ul className="list-disc pl-5">
        <li>
          <strong>Roles.</strong> Customer is controller of Fusion HCM personal data. SLP is processor for Orca-hosted
          data (accounts, connections, campaigns, prompt audit).
        </li>
        <li>
          <strong>Instructions.</strong> We process only to provide Orca and as documented in Trust &amp; data flow.
        </li>
        <li>
          <strong>Subprocessors.</strong> Listed on /subprocessors. We will notify material changes for subscribed
          customers.
        </li>
        <li>
          <strong>Security.</strong> TLS in transit, encrypted connection secrets, session cookies, optional SSO, access
          limited to customer-designated users.
        </li>
        <li>
          <strong>International transfers.</strong> Depend on Heroku/Atlas/LLM region chosen for the deployment. OCI is
          the recommended path when data residency is required.
        </li>
        <li>
          <strong>Assistance.</strong> We will assist with data-subject requests that concern Orca-hosted records.
          Fusion records stay in the customer’s pod.
        </li>
        <li>
          <strong>Deletion.</strong> Within 30 days after contract end, unless law requires retention.
        </li>
      </ul>
    </LegalShell>
  );
}

export function TrustPage({ navigate }) {
  return (
    <LegalShell title="Trust & data flow">
      <p className="rounded-lg border border-amber-800/50 bg-amber-950/30 p-3 text-amber-100">
        We will not claim “data stays in OCI” unless OCI Generative AI is the configured provider for that customer.
        Some public deployments currently use DeepSeek for chat. Ask us which path your pod will use before you send
        production entitlements.
      </p>
      <h2 className="text-lg font-semibold text-orca-strong">Flow</h2>
      <ol className="list-decimal space-y-2 pl-5">
        <li>Admin signs into Orca (password or WorkOS SSO).</li>
        <li>Admin saves a Fusion Connection (host + integration user). Password is encrypted at rest.</li>
        <li>Lookups, reports, BPM metadata, and SQL runner calls go from Orca’s server to the customer Fusion pod.</li>
        <li>Copilot prompts + retrieved context go to the configured LLM (OCI or DeepSeek or mock).</li>
        <li>Orca stores campaigns, SoD rules, drafts, and an audit of AI interactions in MongoDB.</li>
        <li>Writes back to Fusion (approval deploy, role revoke) happen only on an explicit user action.</li>
      </ol>
      <h2 className="text-lg font-semibold text-orca-strong">What we do not do</h2>
      <ul className="list-disc pl-5">
        <li>We do not scrape your entire HCM worker PII into a warehouse by default.</li>
        <li>We do not embed the Fusion UI.</li>
        <li>We do not auto-regenerate ACLs via Oracle’s 26C assistant.</li>
      </ul>
      <h2 className="text-lg font-semibold text-orca-strong">Controls (SOC 2 not yet attested)</h2>
      <ul className="list-disc pl-5">
        <li>HTTPS, HttpOnly session cookies, Mongo session store in production.</li>
        <li>AES-GCM for saved Fusion passwords when HCM_ENCRYPTION_KEY is set.</li>
        <li>Optional WorkOS SSO (Okta, Entra ID, others).</li>
        <li>Public /api/health no longer exposes LLM URLs, feature flags, or HCM usernames.</li>
      </ul>
      <button type="button" onClick={() => navigate('/subprocessors')} className="text-cyan-400">
        Subprocessors →
      </button>
    </LegalShell>
  );
}

export function SubprocessorsPage() {
  return (
    <LegalShell title="Subprocessors">
      <div className="overflow-hidden rounded-xl border border-orca">
        <table className="w-full text-left text-sm">
          <thead className="bg-orca-raised text-xs uppercase text-slate-400">
            <tr>
              <th className="px-3 py-2">Party</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Region</th>
            </tr>
          </thead>
          <tbody>
            {SUBPROCESSORS.map((row) => (
              <tr key={row.name} className="border-t border-orca">
                <td className="px-3 py-2 text-orca-strong">{row.name}</td>
                <td className="px-3 py-2">{row.role}</td>
                <td className="px-3 py-2">{row.region}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </LegalShell>
  );
}
